'use client'

import { usePathname } from 'next/navigation'
import { styled } from 'styled-components'

import { type GlobalHeaderNavData } from '@b-stock/fe-core/bstock-react'
import { Breakpoints } from '@b-stock/fe-core/design-system'

import type { MarketplaceAuctionNav } from '@/helpers/cms/marketplaceNav'

import { GlobalHeaderWrapper } from './globalHeader'
import { HeaderAnalyticsProvider } from './globalHeader/HeaderAnalyticsProvider'

// padding-bottom reserves space below the header when the auction nav row
// isn't rendered, so header height stays consistent across pages. On the
// storefront route, StorefrontTabs sits flush against the header instead —
// that reserved space would double as invisible, still-clickable header
// padding on top of the tab bar, so drop it to 0 there.
function getHeaderPaddingBottom(props: {
  $showAuctionNav: boolean
  $isStorefrontPage: boolean
}): string {
  if (props.$showAuctionNav) return '0.25rem'
  if (props.$isStorefrontPage) return '0'
  return '1.5rem'
}

// Without z-index here, nav dropdowns are behind FiltersTopBar. Must also
// stay above StorefrontTabs' LogoAndTabBar (z-index: 26) so the header's own
// search suggestions dropdown isn't painted over by the storefront tab row.
const HeaderContainer = styled.header<{
  $showAuctionNav: boolean
  $isStorefrontPage: boolean
}>`
  --headerPadding: 1.5rem;
  padding-bottom: ${getHeaderPaddingBottom};
  position: relative;
  z-index: 27;

  @media ${Breakpoints.min.large} {
    --headerPadding: 1.5rem;
  }
`

// NOTE: This function is still needed after unified header rollout
// to determine when to use variant="storefront" vs variant="default"
function shouldUseBuyHeader(pathname: string | null): boolean {
  if (!pathname) return false

  // Specific routes that should use storefront variant:
  // 1. Seller storefront: /buy/seller/[sellerSlug]
  // 2. Seller details: /buy/sellers/details/[sellerSlug]
  // 3. Listing details: /buy/listings/details/[hexadecimal-id]

  const buyHeaderPatterns = [
    /^\/buy\/seller\/[^/]+\/?$/,
    /^\/buy\/sellers\/details\/[^/]+\/?$/,
    /^\/buy\/listings\/details\/[a-f0-9]+\/?$/i,
  ]

  return buyHeaderPatterns.some((pattern) => pattern.test(pathname))
}

export default function Header({
  hasHomeIntent = false,
  navData,
  auctionNav,
}: {
  hasHomeIntent?: boolean
  /**
   * Nav content from the CMS, fetched in the root layout. Threaded through rather than fetched
   * here because this is a client component; the flag deciding whether to use it is read one
   * level down, where the rest of the header's flags already live.
   */
  navData?: GlobalHeaderNavData
  /** Second-row nav content from the CMS, threaded the same way. */
  auctionNav?: MarketplaceAuctionNav
}) {
  const pathname = usePathname()

  const isStorefrontPage = shouldUseBuyHeader(pathname)

  return (
    <HeaderAnalyticsProvider pathname={pathname} hasHomeIntent={hasHomeIntent}>
      <HeaderContainer
        $showAuctionNav={!isStorefrontPage}
        $isStorefrontPage={isStorefrontPage}
      >
        <GlobalHeaderWrapper
          variant={isStorefrontPage ? 'storefront' : 'default'}
          showAuctionNav={!isStorefrontPage}
          navData={navData}
          auctionNav={auctionNav}
        />
      </HeaderContainer>
    </HeaderAnalyticsProvider>
  )
}
